import { supabase } from '@/utils/supabase';

/**
 * Núcleo de sessão/token da área UPA VET (Contrato de Uso da API v2.0).
 *
 * Responsabilidade: entregar SEMPRE um access_token válido para as chamadas ao
 * PostgREST da VPS, de forma automática e transparente para o usuário.
 *
 * Estratégia de renovação:
 *  1. PROATIVA — antes de cada requisição checamos a expiração do JWT. Se ele
 *     estiver dentro da margem de segurança, renovamos ANTES de sair. Assim o
 *     usuário não toma um 401 no meio do uso.
 *  2. REATIVA (seção 9) — se, ainda assim, a API responder 401, renovamos e a
 *     requisição é repetida uma vez (feito no cliente da API).
 *  3. DEDUPLICADA — várias chamadas que caem ao mesmo tempo compartilham UMA
 *     única renovação em andamento (evita "tempestade" de refreshSession).
 */

/** Margem de segurança, em segundos, antes da expiração para renovar proativamente. */
export const MARGEM_RENOVACAO_S = 90;

let tokenEmMemoria: string | null = null;
let renovacaoEmAndamento: Promise<string | null> | null = null;

/**
 * Mantém o access_token em memória sincronizado com a sessão do Supabase
 * (Contrato v2.0, seção 4). O callback é SÍNCRONO de propósito: apenas atualiza
 * o estado local, nunca dispara novas chamadas ao Supabase dentro de si mesmo.
 */
supabase.auth.onAuthStateChange((_event, session) => {
  tokenEmMemoria = session?.access_token ?? null;
});

/** Decodifica o campo `exp` (epoch em segundos) de um JWT, sem validar a assinatura. */
export function expDoToken(token: string | null): number | null {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1] ?? '';
    const normalizado = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalizado));
    return typeof payload?.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** true quando o token está ausente ou expira dentro da margem de segurança. */
export function tokenPrestesAExpirar(
  token: string | null,
  margemS = MARGEM_RENOVACAO_S,
): boolean {
  const exp = expDoToken(token);
  if (exp === null) return true;
  return exp * 1000 - Date.now() <= margemS * 1000;
}

/** Devolve o token mantido em memória (pode ser null se não houver sessão). */
export function tokenEmMemoriaAtual(): string | null {
  return tokenEmMemoria;
}

/** Lê o token atual, preferindo o valor mantido em memória. */
async function lerToken(): Promise<string | null> {
  if (tokenEmMemoria) return tokenEmMemoria;
  try {
    const { data } = await supabase.auth.getSession();
    tokenEmMemoria = data.session?.access_token ?? null;
    return tokenEmMemoria;
  } catch {
    return null;
  }
}

/**
 * Renova a sessão do Supabase e devolve o novo access_token (ou null em falha).
 * Chamadas concorrentes compartilham a MESMA Promise de renovação.
 */
export function renovarSessao(): Promise<string | null> {
  if (renovacaoEmAndamento) return renovacaoEmAndamento;

  renovacaoEmAndamento = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) return null;
      tokenEmMemoria = data.session?.access_token ?? null;
      return tokenEmMemoria;
    } catch {
      return null;
    } finally {
      renovacaoEmAndamento = null;
    }
  })();

  return renovacaoEmAndamento;
}

/**
 * Garante um token utilizável para a próxima requisição:
 *  - sem sessão → null;
 *  - token perto de expirar → renova proativamente;
 *  - renovação falha mas o token ainda é válido → devolve o token atual (tenta mesmo assim).
 */
export async function garantirTokenValido(): Promise<string | null> {
  const token = await lerToken();
  if (!token) return null;

  if (tokenPrestesAExpirar(token)) {
    const renovado = await renovarSessao();
    return renovado ?? token;
  }

  return token;
}